import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import useTypingListener from '../../hooks/useTypingListener';
import {useChat} from "../../contexts/ChatProvider";
import { hexToRgb } from '../../utils/helper';
import ArrowIcon from '../icons/SendIcon';
import AttachmentIcon from '../icons/AttachmentIcon';
import { useLocale } from '../../contexts/LocaleProvider';

const Container = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  display: flex;
`;

const Form = styled.form`
  background-color: ${({ backgroundColor, isFullScreen }) => isFullScreen ? "transparent" : backgroundColor || "#f3f4f6"};
  padding-top: 8px;
  padding-bottom: 8px;
  border-bottom-right-radius: ${({isFullScreen}) => isFullScreen ? "" : "16px"};
  border-bottom-left-radius: ${({isFullScreen}) => isFullScreen ? "" : "16px"};
  border-radius: ${({isFullScreen}) => isFullScreen ? "12px" : ""};
  box-shadow: ${({isFullScreen}) => isFullScreen ? "none" : "0px -1px 0px rgba(0, 0, 0, 0.07999999821186066)"};
  border: ${({isFullScreen}) => isFullScreen && "1px solid #aeaeae"};
  position: relative;
  width: 100%;
  display: flex;
  align-items: end;
  box-sizing: border-box;
  &:hover{
    border: ${({isFullScreen}) => isFullScreen && "1px solid grey"};
  }
  &:focus-within {
    border: ${({isFullScreen}) => isFullScreen && "1px solid grey"};
  }
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const InputBackground = styled.div`
  width: 100%;
  border-radius: 0.7rem;
  background-color: ${({ bgColor, showOpacity, isFullScreen }) => isFullScreen ? "transparent" :
    showOpacity ? `rgba(${hexToRgb(bgColor)}, 0.4)` : bgColor};
  border: ${({isFullScreen}) => isFullScreen ? "none" : "1px solid #ecebeb"};
  padding: ${({isFullScreen}) => isFullScreen ? "0px" : "8px"};
  padding-bottom:0;
`;

const InputElement = styled.textarea`
  width: 100%;
  font-size: 14px;
  color: ${({ color }) => color || "rgba(0,0,0,.87)"};
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  background-color: transparent;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  padding-left: ${({isFullScreen}) => isFullScreen ? "0px" : "8px"};
  padding-right: 8px;
  overflow:auto;
  resize: none;
  line-height: 1.6em;
  min-height: 1.6em;
  max-height: 4.8em;

  &::placeholder {
    color:  ${({ placeHoldercolor }) => placeHoldercolor || 'rgba(0,0,0,.4)'};
  }
  
  &:focus {
    outline: none;
  }
`;


const ArrowContainer = styled.div`
  position: relative;
  padding-left: 16px;
  padding-right: 16px;
  cursor: ${({ showcursor, disabled }) => showcursor && !disabled ? 'pointer' : 'default'};
  display: flex;
  align-items: end;
  opacity: ${({ showcursor, disabled }) => showcursor && !disabled ? '1' : '0.4'};
  height: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
`;

const AttachmentContainer = styled.div`
  position: relative;
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  align-items: end;
  height: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
`;

const AttachPlaceholder = styled.div`
  position: relative;
  padding: 12px;
`;

const SendPlaceholder = styled.div`
  position: relative;
  padding: 12px;
`;

export default function MessageInput({
  onSendMessage,
  mobileView,
  onStartTyping,
  onEndTyping,
  showAttachButton = true,
  showSendButton = true,
  disabled = false,
  onAttachClick,
  placeholder = 'Send a message...',
  onKeyDown,
  onKeyUp,
  isFullScreen=false
}) {
  const { t } = useLocale();
  const { theme, colorSet, disableInput, sessionTransition, sessionSwitch } = useChat();
  const backgroundColor = colorSet["--input-background-color"];
  const inputTextColor = colorSet["--input-text-color"];
  const inputAttachColor = colorSet["--input-attach-color"];
  const inputSendColor = colorSet["--input-send-color"];
  const inputElementColor = colorSet["--input-element-color"];
  const inputPlaceholderColor = colorSet["--input-placeholder-color"];
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const { setTyping, ...inputProps } = useTypingListener({ onStartTyping, onEndTyping });
  const [composing, setComposing] = useState(false);

  const resizeTextArea = () => {
    inputRef.current.style.height = "auto";
    inputRef.current.style.height = inputRef.current.scrollHeight + "px";
  };
  const handleSubmit = () => {
    if (!disabled && text.trim().length > 0) {
      inputRef.current.innerText = '';
      setTyping(false);
      onSendMessage && onSendMessage(text.trim());
      setText("");
    }
  };
  const handleKeyDown = (e) => {
    if (!e.shiftKey && e.keyCode === 13 && !composing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(resizeTextArea, [text]);
  useEffect(() => {
    if(disableInput || sessionTransition || sessionSwitch) setText("");
  }, [disableInput, sessionTransition, sessionSwitch])

  return (
    <Container mobile={mobileView}>
      <Form
        isFullScreen={isFullScreen}
        backgroundColor={backgroundColor}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {showAttachButton ? (
          <AttachmentContainer
            disabled={disabled}
            onClick={onAttachClick}
          >
            <AttachmentIcon  size={24} color={inputAttachColor || theme}/>
          </AttachmentContainer>
        ) : (
          <AttachPlaceholder />
        )}
        <InputContainer>
        <InputBackground bgColor={inputElementColor || theme} showOpacity={inputElementColor ? false : true} isFullScreen={isFullScreen}>
        <InputElement
            disabled={disableInput}
            isFullScreen={isFullScreen}
            type="text"
            color={inputTextColor}
            ref={inputRef}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
            onCompositionStart={() => {
              setComposing(true);
            }}
            onCompositionEnd={() => {
              setComposing(false);
            }}
            placeholder={disableInput ? t("sessionExpired.inputPlaceholder") : placeholder}
            placeHoldercolor={inputPlaceholderColor}
            rows={1}
            onKeyDown={handleKeyDown}
            autoFocus={false}
          />
        </InputBackground>  
        </InputContainer>
        {showSendButton ? (
          <ArrowContainer
            disabled={disabled}
            showcursor={text.trim().length > 0}
            onClick={handleSubmit}
          >
            <ArrowIcon color={inputSendColor || theme} />
          </ArrowContainer>
        ) : (
          <SendPlaceholder />
        )}
      </Form>
    </Container>
  );
}
